import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import moment from 'moment';
import { useFormFields, formatStatus } from 'helpers';
import {
  Row,
  Col,
  Alerts,
  ButtonAction,
  Modal,
  Container,
  PageContent,
  Pagination,
  Table,
  FiltersControls,
  Spinner,
  FiltersCustom,
} from 'components';
import {
  loadMaPeopleMembersCreator,
  setMaPeopleMembersSortCreator,
  setMaPeopleMembersPageCreator,
  setMaPeopleMembersPerPageCreator,
  removeMaPeopleMembersUserCreator,
  setMaPeopleMembersFiltersCreator,
  downloadMaPeopleReportCreator,
  acceptMaPeopleListUserCreator,
  declineMaPeopleListUserCreator,
} from 'store/actionsCreators';
import { ReactSVG } from 'react-svg';
import { iconList } from 'assets';
import { Margin } from 'styled-components-spacing';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Content, Wrapper, Icon, ActionText, RowStyled } from '../List/List.styled';
import { formatDateTime } from 'helpers/date';

const APPLIED_STATUS = 'Applied';
const YES_STATUS = 'Yes';
const MEMBER_REJECTED_STATUS = 'MemberRejected';

function Members({
  items,
  page,
  pages,
  perPage,
  sort,
  loadUsers,
  setSort,
  loading,
  removeUser,
  acceptUser,
  declineUser,
  setPage,
  setPerPage,
  downloadReport,
  filters,
  setFilters,
  localMemberAssociation,
}) {
  const { t } = useTranslation();
  const [removeConfirmModal, setRemoveConfirmModal] = useState(null);
  const [acceptConfirmModal, setAcceptConfirmModal] = useState(null);
  const [declineConfirmModal, setDeclineConfirmModal] = useState(null);
  const [filterFields, handleFilterFieldChange] = useFormFields({
    firstName: '',
    lastName: '',
    email: '',
    createdFrom: null,
    certified: null,
    youngCrew: null,
    membership: null,
  });

  const simpleOptions = [
    {
      value: 'no',
      label: 'No',
    },
    {
      value: 'yes',
      label: 'Yes',
    },
  ];

  const filtersProps = {
    firstName: {
      type: 'input',
      name: 'first-name',
      id: 'f-name',
      label: t('page.ma.peopleAdd.firstName'),
      placeholder: t('forms.firstNamePlaceholder'),
    },
    lastName: {
      type: 'input',
      name: 'last-name',
      id: 'f-lastName',
      label: t('page.ma.peopleAdd.lastName'),
      placeholder: t('forms.lastNamePlaceholder'),
    },
    email: {
      type: 'input',
      name: 'email',
      id: 'f-email',
      label: t('page.ma.peopleAdd.email'),
      placeholder: t('forms.emailPlaceholder'),
    },
    createdFrom: {
      type: 'datepicker',
      name: 'createdFrom',
      id: 'f-createdFrom',
      label: t('page.ma.peopleAdd.createdFrom'),
      placeholder: t('forms.createdFromPlaceholder'),
    },
    certified: {
      type: 'select',
      name: 'certified',
      id: 'f-jobTitle',
      label: t('page.ma.peopleAdd.certified'),
      placeholder: t('forms.certifiedPlaceholder'),
      options: simpleOptions,
    },
    youngCrew: {
      type: 'select',
      name: 'youngCrew',
      id: 'f-youngCrew',
      label: t('page.ma.peopleAdd.youngCrew'),
      placeholder: t('forms.youngCrewPlaceholder'),
      options: simpleOptions,
    },
    membership: {
      type: 'select',
      name: 'membership',
      id: 'f-membership',
      label: t('page.ma.peopleAdd.status'),
      placeholder: t('forms.membershipPlaceholder'),
      options: [
        { value: 'Yes', label: 'Yes' },
        { value: 'Applied', label: 'Applied' },
        { value: 'MemberRejected', label: 'Member Rejected' },
      ],
    },
  };

  useEffect(() => {
    setPage(1);
  }, [perPage, filters]);

  useEffect(() => {
    loadUsers();
  }, [page, perPage, sort, filters]);

  const handleSortBy = (name) => () => {
    const [currentSortName, currentSortStatus] = sort || [];
    const sortByNewColumn = currentSortName !== name;

    if (sortByNewColumn) {
      setSort([name, 'desc']);
      return;
    }

    const newSortStatus = currentSortStatus === 'desc' ? 'asc' : 'desc';
    setSort([name, newSortStatus]);
  };

  const handleChangePerPage = ({ value }) => {
    setPerPage(value);
  };

  const isCurrentMa = (user) => {
    return (
      user.memberAssociationPeople &&
      user.memberAssociationPeople.memberAssociation.id === localMemberAssociation
    );
  };

  const getUserStatus = (user) => {
    if (!user.memberAssociationPeople || !isCurrentMa(user)) return 'No';
    return formatStatus(user.memberAssociationPeople.status);
  };

  // Set when the membership was confirmed; empty for members accepted before it was recorded.
  const getMemberSince = (user) => {
    if (!user.memberAssociationPeople || !isCurrentMa(user)) return '-';
    return user.memberAssociationPeople.acceptedDate
      ? moment(user.memberAssociationPeople.acceptedDate).format('DD/MM/YYYY')
      : '-';
  };

  const getYoungCrewStatus = (user) => {
    return user.yCPeople && user.yCPeople.status === 'Yes' ? 'Yes' : 'No';
  };

  const handleAction = (name, id) => () => {
    switch (name) {
      case 'remove':
        return removeUser({ user: id });
      case 'accept':
        return acceptUser({ user: id });
      case 'decline':
        return declineUser({ user: id });
      default:
        return console.log('action not defined');
    }
  };

  const downloadButton = () => (
    <Wrapper onClick={() => downloadReport()}>
      <Icon>
        <ReactSVG src={iconList.download} />
      </Icon>
      <Content>
        <p className="word-break">{t('page.ma.peopleMembers.reportTitleMa')}</p>
        <Margin top="4">
          <div className="link uppercase">{t('page.ma.peopleList.downloadReport')}</div>
        </Margin>
      </Content>
    </Wrapper>
  );

  return (
    <>
      <Container>
        <PageContent>
          <Alerts type="ma" />
          <p dangerouslySetInnerHTML={{ __html: t('page.ma.peopleMembers.info') }} />
          <Margin
            as="p"
            top={{ xs: 16 }}
            dangerouslySetInnerHTML={{ __html: t('page.ma.peopleMembers.info2') }}
          />
          <Margin top={{ xs: 16 }}>
            <FiltersCustom
              title={t('page.ma.peopleAdd.filterTitle')}
              filters={filtersProps}
              filterFields={filterFields}
              handleFilterFieldChange={handleFilterFieldChange}
              onFiltersApply={setFilters}
            />
          </Margin>
          <Margin top={{ xs: 16 }}>
            <RowStyled>
              {downloadButton()}
              <FiltersControls perPageOnChange={handleChangePerPage} perPageSelected={perPage} />
            </RowStyled>
          </Margin>
          <Table tablebreakpoint="lg">
            <Table.Head>
              <Table.Row>
                <Table.Heading sort={handleSortBy('firstLastName')}>
                  {t('page.ma.peopleMembers.firstAndLastName')}
                </Table.Heading>
                <Table.Heading sort={handleSortBy('email')}>
                  {t('page.ma.peopleMembers.email')}
                </Table.Heading>
                <Table.Heading sort={handleSortBy('createdAt')}>
                  {t('page.ma.peopleList.createdAt')}
                </Table.Heading>
                <Table.Heading sort={handleSortBy('role')}>
                  {t('page.ma.peopleMembers.certified')}
                </Table.Heading>
                <Table.Heading sort={handleSortBy('date')}>
                  {t('page.ma.peopleMembers.membershipStatus')}
                </Table.Heading>
                <Table.Heading>{t('page.ma.peopleMembers.memberSince')}</Table.Heading>
                <Table.Heading>{t('page.ma.peopleList.youngCrewMember')}</Table.Heading>
                <Table.Heading />
              </Table.Row>
            </Table.Head>
            <Table.Body>
              {loading ? (
                <Table.Row>
                  <Table.Cell colSpan={5}>
                    <Spinner />
                  </Table.Cell>
                </Table.Row>
              ) : null}
              {!loading && !items.length ? (
                <Table.Row>
                  <Table.Cell colSpan={4}>No results found</Table.Cell>
                </Table.Row>
              ) : null}
              {!loading && items.length
                ? items.map((user) => (
                    <Table.Row key={user.id}>
                      <Table.Cell>
                        <Table.Label>{t('page.ma.peopleMembers.firstAndLastName')}:</Table.Label>
                        <Link to={`/user-profile/${user.uniqueUrl}`}>
                          {`${user.firstName} ${user.lastName}`}
                        </Link>
                      </Table.Cell>
                      <Table.Cell>
                        <Table.Label>{t('page.ma.peopleMembers.email')}:</Table.Label>
                        {user.email}
                      </Table.Cell>
                      <Table.Cell>
                        <Table.Label>{t('page.ma.peopleUsers.createdAt')}:</Table.Label>
                        {user.createdAt && formatDateTime(user.createdAt, 'DD/MM/YYYY')}
                      </Table.Cell>
                      <Table.Cell>
                        <Table.Label>{t('page.ma.peopleMembers.certified')}:</Table.Label>
                        {user.certificates.length ? 'Yes' : 'No'}
                      </Table.Cell>
                      <Table.Cell>
                        <Table.Label>{t('page.ma.peopleMembers.membershipStatus')}:</Table.Label>
                        {getUserStatus(user)}
                      </Table.Cell>
                      <Table.Cell>
                        <Table.Label>{t('page.ma.peopleMembers.memberSince')}:</Table.Label>
                        {getMemberSince(user)}
                      </Table.Cell>
                      <Table.Cell>
                        <Table.Label>{t('page.ma.peopleList.youngCrewMember')}:</Table.Label>
                        {getYoungCrewStatus(user)}
                      </Table.Cell>
                      <Table.Cell>
                        {isCurrentMa(user) ? (
                          <>
                            {user.memberAssociationUserRoles &&
                            user.memberAssociationUserRoles.length > 0 ? (
                              <ActionText>MA Officer</ActionText>
                            ) : (
                              <Row>
                                {user.memberAssociationPeople &&
                                user.memberAssociationPeople.status === APPLIED_STATUS ? (
                                  <>
                                    <Col size={{ md: 2 / 5, xl: 1 / 2 }}>
                                      <ButtonAction
                                        onClick={() => setAcceptConfirmModal(user.id)}
                                        title={t('global.acceptButton')}
                                        icon="checkCircle"
                                      />
                                    </Col>
                                    <Col size={{ md: 2 / 5, xl: 1 / 2 }}>
                                      <ButtonAction
                                        onClick={() => setDeclineConfirmModal(user.id)}
                                        title={t('global.declineButton')}
                                        icon="closeCircle"
                                      />
                                    </Col>
                                  </>
                                ) : null}
                                {user.memberAssociationPeople &&
                                user.memberAssociationPeople.status === YES_STATUS ? (
                                  <Col size={{ md: 2 / 5, xl: 1 / 2 }}>
                                    <ButtonAction
                                      onClick={() => setRemoveConfirmModal(user.id)}
                                      title={t('global.removeButton')}
                                      icon="closeCircle"
                                    />
                                  </Col>
                                ) : null}
                                {user.memberAssociationPeople &&
                                user.memberAssociationPeople.status === MEMBER_REJECTED_STATUS ? (
                                  <Col size={{ md: 2 / 5, xl: 1 / 2 }}>
                                    <ButtonAction
                                      onClick={() => setAcceptConfirmModal(user.id)}
                                      title={t('global.acceptButton')}
                                      icon="checkCircle"
                                    />
                                  </Col>
                                ) : null}
                              </Row>
                            )}
                          </>
                        ) : null}
                      </Table.Cell>
                    </Table.Row>
                  ))
                : null}
            </Table.Body>
          </Table>
          <Pagination styled currentPage={page} totalPages={pages} onChange={setPage} />
        </PageContent>
      </Container>
      <Modal
        heading={t('page.ma.peopleMembers.modalDeleteUser')}
        isOpen={!!removeConfirmModal}
        confirm={() => handleAction('remove', removeConfirmModal)() && setRemoveConfirmModal(null)}
        cancel={() => setRemoveConfirmModal(null)}
      />
      <Modal
        heading={t('page.ma.peopleList.modalAcceptUser')}
        isOpen={!!acceptConfirmModal}
        confirm={() => handleAction('accept', acceptConfirmModal)() && setAcceptConfirmModal(null)}
        cancel={() => setAcceptConfirmModal(null)}
      />
      <Modal
        heading={t('page.ma.peopleList.modalDeclineUser')}
        isOpen={!!declineConfirmModal}
        confirm={() =>
          handleAction('decline', declineConfirmModal)() && setDeclineConfirmModal(null)
        }
        cancel={() => setDeclineConfirmModal(null)}
      />
    </>
  );
}

Members.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  page: PropTypes.number.isRequired,
  pages: PropTypes.number.isRequired,
  perPage: PropTypes.number.isRequired,
  loadUsers: PropTypes.func.isRequired,
  setSort: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  removeUser: PropTypes.func.isRequired,
  acceptUser: PropTypes.func.isRequired,
  declineUser: PropTypes.func.isRequired,
  setPage: PropTypes.func.isRequired,
  setPerPage: PropTypes.func.isRequired,
  sort: PropTypes.arrayOf([PropTypes.string, PropTypes.string]),
  downloadReport: PropTypes.func.isRequired,
  filters: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  setFilters: PropTypes.func.isRequired,
  localMemberAssociation: PropTypes.number.isRequired,
};

Members.defaultProps = {
  sort: [],
};

const mapStateToProps = ({ ma, loading }) => ({
  ...ma.people.members,
  loading: !!loading.filter((item) => item.type === 'MA').length,
});

const mapDispatchToProps = (dispatch) => ({
  loadUsers: (payload) => dispatch(loadMaPeopleMembersCreator(payload)),
  setSort: (payload) => dispatch(setMaPeopleMembersSortCreator(payload)),
  setPage: (payload) => dispatch(setMaPeopleMembersPageCreator(payload)),
  setPerPage: (payload) => dispatch(setMaPeopleMembersPerPageCreator(payload)),
  removeUser: (payload) => dispatch(removeMaPeopleMembersUserCreator(payload)),
  acceptUser: (payload) => dispatch(acceptMaPeopleListUserCreator(payload)),
  declineUser: (payload) => dispatch(declineMaPeopleListUserCreator(payload)),
  downloadReport: () => dispatch(downloadMaPeopleReportCreator('members')),
  setFilters: (payload) => dispatch(setMaPeopleMembersFiltersCreator(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Members);
